import { PERMISSIONS, type Permission } from './permissions.catalog';

interface PermissionRule {
  // Enabling this permission also enables these; disabling any of these cascades
  // back and disables this permission too.
  requires?: Permission[];
  // Enabling this permission also enables these, but disabling them has no
  // effect on this permission (one-way, no reverse cascade).
  enables?: Permission[];
  // Enabling this permission also disables these.
  excludes?: Permission[];
}

export const PERMISSION_RULES: Partial<Record<Permission, PermissionRule>> = {
  [PERMISSIONS.wallet.Transactions]: {
    requires: [PERMISSIONS.wallet.TransactionsBasic],
  },
  [PERMISSIONS.wallet.TransactionsBasic]: {
    enables: [PERMISSIONS.wallet.FinancialFolder],
  },
  [PERMISSIONS.wallet.Invoices]: {
    enables: [PERMISSIONS.wallet.FinancialFolder],
  },
  [PERMISSIONS.wallet.Quotes]: {
    enables: [PERMISSIONS.wallet.FinancialFolder],
  },
  [PERMISSIONS.logbook.DailyLogSignCaptain]: {
    excludes: [PERMISSIONS.logbook.DailyLogSignMate],
  },
  [PERMISSIONS.logbook.DailyLogSignMate]: {
    excludes: [PERMISSIONS.logbook.DailyLogSignCaptain],
  },
};

/**
 * A cascade must not reach a permission the target cannot hold. The pairs here do not
 * always live or die together in the seat allow-lists — a `navigate` vessel may grant
 * `Transactions` while `TransactionsBasic` stays behind the plan — and the People service
 * accepts an out-of-allow-list permission on write without ever reading it back, so
 * cascading into one would store a right that silently does nothing and show the owner a
 * disabled row flipping on its own. `isBlocked` names those, and they are left untouched
 * in both directions.
 */
export const applyPermissionRules = (
  next: Set<string>,
  rightName: string,
  enabled: boolean,
  isBlocked: (name: string) => boolean = () => false,
) => {
  const rule = PERMISSION_RULES[rightName as Permission];

  if (enabled) {
    for (const required of [...(rule?.requires ?? []), ...(rule?.enables ?? [])]) {
      if (!next.has(required) && !isBlocked(required)) {
        next.add(required);
        applyPermissionRules(next, required, true, isBlocked);
      }
    }
    for (const excluded of rule?.excludes ?? []) {
      if (next.has(excluded) && !isBlocked(excluded)) {
        next.delete(excluded);
        applyPermissionRules(next, excluded, false, isBlocked);
      }
    }
    return;
  }

  for (const [name, otherRule] of Object.entries(PERMISSION_RULES)) {
    if (
      otherRule.requires?.includes(rightName as Permission) &&
      next.has(name) &&
      !isBlocked(name)
    ) {
      next.delete(name);
      applyPermissionRules(next, name, false, isBlocked);
    }
  }
};
